import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableContainer, TableHead, TableRow, TableCell, Button, IconButton, Paper } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { getAllBlogs } from '../../services/getAllApi';
import { createBlog, deleteBlog, updateBlog } from '../../services/getAdminApi';
import '../../styles/AdminGeneral.css';

const AddBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    blog_image: null,
    link: '',
    date: '', // renamed from published_at to date
  });
  const [fileName, setFileName] = useState({
    blog_image: '',
  });
  const [editingId, setEditingId] = useState(null);

  const fetchBlogs = async () => {
    try {
      const response = await getAllBlogs();
      setBlogs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch blogs', 'error');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      setFormData({
        ...formData,
        [name]: files[0],
      });
      setFileName({
        ...fileName,
        [name]: files[0].name,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description || !formData.link) {
      Swal.fire('Validation Error', 'Please fill all required fields', 'error');
      return;
    }

    // Prepare JSON payload instead of FormData
    const payload = {
      title: formData.title,
      description: formData.description,
      link: formData.link,
      date: formData.date,
    };

    // Note: blog_image upload is not handled in JSON payload; needs separate handling if required

    try {
      if (editingId !== null) {
        const confirmEdit = await Swal.fire({
          title: 'Are you sure?',
          text: 'Do you want to update this blog?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, update it!',
        });

        if (confirmEdit.isConfirmed) {
          await updateBlog(editingId, payload).then((res) => {
            if (res.status === 200) {
              Swal.fire('Success', 'Blog updated successfully', 'success');
            }
          });
        }
      } else {
        // For createBlog, still using FormData as image upload is needed
        const formPayload = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
          if (value) formPayload.append(key, value);
        });
        await createBlog(formPayload).then((res) => {
          if (res.status === 201) {
            Swal.fire('Success', 'Blog added successfully', 'success');
          }
        });
      }

      setFormData({
        title: '',
        description: '',
        blog_image: null,
        link: '',
        date: '', // reset date
      });
      setFileName({
        blog_image: '',
      });
      setEditingId(null);
      document.getElementById('file-upload').value = '';
      fetchBlogs();
    } catch (error) {
      Swal.fire('Error', 'Failed to save blog', 'error');
    }
  };

  const handleEdit = (blog) => {
    setEditingId(blog.id);
    setFormData({
      title: blog.title,
      description: blog.description,
      blog_image: blog.blog_image,
      link: blog.link,
      date: blog.published_at ? blog.published_at.split('T')[0] : '', // renamed to date
    });
    setFileName({
      blog_image: blog.blog_image ? blog.blog_image.split('/').pop() : '',
    });
  };

  const handleDelete = async (id) => {
    const confirmDelete = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action will delete the blog permanently!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
    });

    if (confirmDelete.isConfirmed) {
      try {
        await deleteBlog(id).then((res) => {
          if (res.status === 200) {
            Swal.fire('Success', 'Blog deleted successfully', 'success');
          }
        });

        fetchBlogs();
      } catch (error) {
        Swal.fire('Error', 'Failed to delete blog', 'error');
      }
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  return (
    <div>
      <div className="dashboard-heading-div">
        <p>Manage Blogs</p>
      </div>

      <div className="editing-container">
        <div>
          <h2 className="editing-container-heading">{editingId !== null ? 'Edit Blog' : 'Create Blog'}</h2>

          <div className="editing-container-form">
            <div className="editing-container-input-div">
              <label>Title: </label>
              <input
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Enter Blog Title"
              />
            </div>

            <div className="editing-container-input-div">
              <label>Description: </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                placeholder="Enter Blog Description"
              />
            </div>

            <div className="editing-container-input-div">
              <label>Image:</label>
              <label htmlFor="file-upload" className="file-upload-btn">
                <i className="bx bx-cloud-upload"></i> {fileName.blog_image || ' Choose File'}
              </label>
              <input
                className="file-upload"
                id="file-upload"
                type="file"
                name="blog_image"
                accept="image/*"
                onChange={handleInputChange}
              />
            </div>

            <div className="editing-container-input-div">
              <label>Link: </label>
              <input
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                required
                placeholder="Enter Blog Link"
              />
            </div>

            {/* 🌟 Add Published On Date Field */}
            <div className="editing-container-input-div">
              <label>Published On: </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                placeholder="Select published date"
              />
            </div>
          </div>
        </div>

        <button variant="contained" color="primary" onClick={handleSave}>
          {editingId !== null ? 'Update' : 'SAVE'} <i className="bx bx-send"></i>
        </button>
      </div>

      <div className="table-container-registered-students">
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Description</th>
              <th>Image</th>
              <th>Link</th>
              <th>Published On</th> {/* 🌟 Add column */}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {blogs.map((blog) => (
              <tr key={blog.id}>
                <td>{blog.title}</td>
                <td>{blog.description}</td>
                <td>
                  {blog.blog_image && (
                    <img
                      src={`${"https://console.fbsedu.in/storage/app/"}/public/${blog.blog_image}`}
                      alt="Blog"
                      style={{ width: 50, height: 50 }}
                    />
                  )}
                </td>
                <td>
                  <a href={blog.link} target="_blank" rel="noopener noreferrer">
                    {blog.link}
                  </a>
                </td>
                <td>
                {blog.date
                    ? new Date(blog.date).toLocaleDateString()
                    : 'N/A'}
                </td>
                <td>
                  <IconButton color="primary" onClick={() => handleEdit(blog)}>
                    <Edit />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDelete(blog.id)}>
                    <Delete />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AddBlogs;
